import { Howl } from 'howler';
import { Reasons } from '../utils/Reason';
import audioAtlas from 'json/audio-atlas.json';

type AudioAtlas = {
  resources: string[];
  spritemap: { [key: string]: { start: number; end: number } };
};

export enum MuteReason {
  AppPaused,
  UserAction,
}

export class SoundManager {
  private soundEnabled: boolean = true;
  private readonly sound: Howl;
  private soundMuteReasons: Reasons<MuteReason> = new Reasons();
  private readonly idByName: { [key: string]: number } = {};
  private frameSounds: string[] = [];

  constructor() {
    this.sound = new Howl({
      src: ['audio/sprite.ogg', 'audio/sprite.mp3'],
      sprite: Object.fromEntries(
        Object.entries((audioAtlas as AudioAtlas).spritemap).map(([key, value]) => [
          key,
          [Math.round(value.start * 1000), Math.round((value.end - value.start) * 1000)],
        ]),
      ),
    });

    this.soundMuteReasons.onChange.add((value) => this.handleSoundMuteChange(value));
  }

  public init(soundEnabled: boolean): void {
    this.setSoundEnabled(soundEnabled, MuteReason.UserAction);
  }

  public play(soundName: string): Promise<void> {
    if (this.sound.state() !== 'loaded' || this.frameSounds.includes(soundName)) {
      return Promise.resolve();
    }

    this.frameSounds.push(soundName);
    const id: number = this.sound.play(soundName);
    this.idByName[soundName] = id;

    return new Promise((resolve) => this.sound.once('end', resolve, id));
  }

  public isPlaying(soundName: string): boolean {
    const id: number | undefined = this.idByName[soundName];
    return id ? this.sound.playing(id) : false;
  }

  public pause(reason: MuteReason): void {
    this.setSoundEnabled(false, reason);
  }

  public resume(reason: MuteReason): void {
    this.setSoundEnabled(true, reason);
  }

  public getSoundEnabled(): boolean {
    return this.soundEnabled;
  }

  public setSoundEnabled(value: boolean, reason: MuteReason) {
    value ? this.soundMuteReasons.remove(reason) : this.soundMuteReasons.add(reason);
  }

  private handleSoundMuteChange(mute: boolean): void {
    this.sound.volume(mute ? 0 : 1);
    this.soundEnabled = !mute;
  }

  public update(): void {
    this.frameSounds.length = 0;
  }
}
